'use client'

/**
 * Ghost Protocol Diagrams
 * Visual components for the ghost protocol blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

const agents = [
  { name: 'Flight', role: 'Architect', color: '#FDC304', letter: 'F' },
  { name: 'EECOM', role: 'Developer', color: '#008dcc', letter: 'E' },
  { name: 'FIDO', role: 'Quality', color: '#22c55e', letter: 'Q' },
  { name: 'Procedures', role: 'Prompts', color: '#a855f7', letter: 'P' },
  { name: 'PAO', role: 'Docs', color: '#06b6d4', letter: 'D' },
  { name: 'CAPCOM', role: 'Integration', color: '#f97316', letter: 'C' },
  { name: 'RETRO', role: 'Security', color: '#F90B8A', letter: 'R' },
  { name: 'Scribe', role: 'Logger', color: '#8b5cf6', letter: 'S' },
]

// Diagram 1: Dual Routing Flow
export function DualRoutingDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          DUAL ROUTING
        </h3>
        <p className='text-sm text-muted-foreground'>
          Every task is routed to{' '}
          <span className='font-semibold text-[#F90B8A]'>who</span> (agent) and{' '}
          <span className='font-semibold text-[#008dcc]'>how</span> (thread
          pattern)
        </p>
      </div>

      <div className='flex flex-col items-center gap-4'>
        {/* Task input */}
        <div className='px-5 py-3 bg-muted rounded-xl border border-border/60 text-center'>
          <p className='text-xs text-muted-foreground mb-1'>TASK</p>
          <p className='text-sm font-semibold text-foreground'>
            &quot;Build payment system: schema, API, checkout, tests&quot;
          </p>
        </div>

        {/* Arrow down */}
        <div className='w-0.5 h-6 bg-[var(--border)]' />

        {/* Coordinator */}
        <div className='px-5 py-2.5 rounded-full border-2 border-foreground/20 bg-background'>
          <p className='text-xs font-bold text-foreground tracking-wider'>
            COORDINATOR
          </p>
        </div>

        {/* Split into two paths */}
        <div className='flex items-start gap-6 md:gap-10 w-full max-w-lg'>
          {/* Left: WHO */}
          <div className='flex-1 flex flex-col items-center gap-3'>
            <div className='w-0.5 h-4 bg-[#F90B8A]/40' />
            <div className='px-4 py-2 rounded-lg bg-[#F90B8A]/10 border border-[#F90B8A]/30'>
              <p className='text-[10px] font-bold text-[#F90B8A] tracking-wider text-center'>
                WHO
              </p>
            </div>
            <div className='flex flex-col gap-2 w-full'>
              {[
                { name: 'EECOM', phase: 'Phase 1-3', color: '#008dcc' },
                { name: 'CAPCOM', phase: 'Phase 2', color: '#f97316' },
                { name: 'FIDO', phase: 'Phase 4', color: '#22c55e' },
                { name: 'RETRO', phase: 'Phase 4', color: '#F90B8A' },
              ].map(a => (
                <div
                  key={a.name}
                  className='flex items-center gap-2 px-3 py-2 rounded-lg bg-background border border-border/60'
                >
                  <div
                    className='w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0'
                    style={{ backgroundColor: a.color }}
                  >
                    <span className='text-[10px] font-bold text-white'>
                      {a.name[0]}
                    </span>
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p className='text-xs font-semibold text-foreground'>
                      {a.name}
                    </p>
                    <p className='text-[10px] text-muted-foreground'>
                      {a.phase}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: HOW */}
          <div className='flex-1 flex flex-col items-center gap-3'>
            <div className='w-0.5 h-4 bg-[#008dcc]/40' />
            <div className='px-4 py-2 rounded-lg bg-[#008dcc]/10 border border-[#008dcc]/30'>
              <p className='text-[10px] font-bold text-[#008dcc] tracking-wider text-center'>
                HOW
              </p>
            </div>
            <div className='w-full px-4 py-4 rounded-xl bg-background border border-border/60'>
              <p className='text-xs font-bold text-[#008dcc] mb-3 text-center'>
                CHAIN THREAD (C-Thread)
              </p>
              <div className='flex flex-col gap-2'>
                {[
                  'Schema',
                  'Stripe API',
                  'Checkout UI',
                  'Tests + Security',
                ].map((phase, i) => (
                  <div key={phase} className='flex items-center gap-2'>
                    <div className='w-5 h-5 rounded-full bg-[#008dcc]/20 border border-[#008dcc]/40 flex items-center justify-center flex-shrink-0'>
                      <span className='text-[9px] font-bold text-[#008dcc]'>
                        {i + 1}
                      </span>
                    </div>
                    <p className='text-[11px] text-foreground/80'>{phase}</p>
                    {i < 3 && (
                      <span className='text-[9px] text-muted-foreground ml-auto'>
                        checkpoint
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Result */}
        <div className='mt-2 px-4 py-2.5 bg-[#22c55e]/10 rounded-lg border border-[#22c55e]/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>Result:</span> 4 specialists execute
            4 sequential phases with human checkpoints between each
          </p>
        </div>
      </div>
    </div>
  )
}

// Diagram 2: Agent Roster
export function AgentRosterDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE 8 AGENTS
        </h3>
        <p className='text-sm text-muted-foreground'>
          Named specialists with charters, boundaries, and persistent memory
        </p>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        {agents.map(agent => (
          <div
            key={agent.name}
            className='flex flex-col items-center gap-2 p-3 md:p-4 rounded-xl bg-background border border-border/60'
          >
            <div
              className='w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center'
              style={{ backgroundColor: agent.color }}
            >
              <span className='text-base md:text-lg font-bold text-white'>
                {agent.letter}
              </span>
            </div>
            <div className='text-center'>
              <p className='text-xs font-bold text-foreground'>{agent.name}</p>
              <p className='text-[10px] text-muted-foreground'>{agent.role}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Coordinator + Scribe note */}
      <div className='mt-4 flex flex-col md:flex-row gap-3'>
        <div className='flex-1 px-4 py-3 rounded-lg bg-[#FDC304]/5 border border-[#FDC304]/20'>
          <p className='text-xs text-foreground'>
            <span className='font-bold' style={{ color: '#FDC304' }}>
              Coordinator
            </span>{' '}
            routes tasks to agents. Does not write code. Selects both the
            specialist and the thread pattern.
          </p>
        </div>
        <div className='flex-1 px-4 py-3 rounded-lg bg-[#8b5cf6]/5 border border-[#8b5cf6]/20'>
          <p className='text-xs text-foreground'>
            <span className='font-bold' style={{ color: '#8b5cf6' }}>
              Scribe
            </span>{' '}
            runs silently in the background after every thread. Logs sessions,
            archives decisions.
          </p>
        </div>
      </div>
    </div>
  )
}

// Diagram 3: Core Four Quality Framework
export function CoreFourDiagram() {
  const dimensions = [
    {
      name: 'Context',
      question: 'Does the work show understanding of the codebase?',
      color: '#008dcc',
      checks: ['Existing patterns followed', 'Agent stayed within charter'],
    },
    {
      name: 'Model',
      question: 'Right capability level applied?',
      color: '#FDC304',
      checks: ['Not over-engineered', 'Right agent assigned'],
    },
    {
      name: 'Prompt',
      question: 'Work aligns with original request?',
      color: '#F90B8A',
      checks: ['No scope drift', 'Handoffs were clean'],
    },
    {
      name: 'Tools',
      question: 'Appropriate tools and tests used?',
      color: '#22c55e',
      checks: ['Tests written', 'Right thread pattern'],
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE CORE FOUR
        </h3>
        <p className='text-sm text-muted-foreground'>
          Every thread is evaluated against four quality dimensions
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
        {dimensions.map(dim => (
          <div
            key={dim.name}
            className='p-4 rounded-xl bg-background border border-border/60'
          >
            <div className='flex items-center gap-2.5 mb-2'>
              <div
                className='w-8 h-8 rounded-lg flex items-center justify-center'
                style={{ backgroundColor: `${dim.color}20` }}
              >
                <span
                  className='text-sm font-bold'
                  style={{ color: dim.color }}
                >
                  {dim.name[0]}
                </span>
              </div>
              <span className='text-sm font-bold text-foreground'>
                {dim.name}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mb-2'>{dim.question}</p>
            <div className='flex flex-col gap-1'>
              {dim.checks.map(check => (
                <div key={check} className='flex items-center gap-1.5'>
                  <div
                    className='w-1 h-1 rounded-full flex-shrink-0'
                    style={{ backgroundColor: dim.color }}
                  />
                  <span className='text-[11px] text-foreground/70'>
                    {check}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Rating scale */}
      <div className='mt-4 flex gap-3'>
        {[
          { label: 'GREEN', desc: 'Merge', color: '#22c55e' },
          { label: 'YELLOW', desc: 'Minor fixes', color: '#FDC304' },
          { label: 'RED', desc: 'Redo', color: '#ef4444' },
        ].map(rating => (
          <div
            key={rating.label}
            className='flex-1 px-3 py-2.5 rounded-lg text-center'
            style={{
              backgroundColor: `${rating.color}10`,
              borderWidth: 1,
              borderColor: `${rating.color}30`,
            }}
          >
            <p className='text-xs font-bold' style={{ color: rating.color }}>
              {rating.label}
            </p>
            <p className='text-[10px] text-muted-foreground'>{rating.desc}</p>
          </div>
        ))}
      </div>
    </div>
  )
}
